/**
 * i18n 검증 로직
 */

import {
  LanguageCode,
  NON_ENGLISH_LANGUAGES,
  getEnabledLanguages,
  getRequiredLanguages,
} from '../../constants/i18n/languages';
import {
  ContentI18nData,
  I18nValidationResult,
  I18nValidationError,
  I18nValidationWarning,
  I18nFieldName,
  I18nFieldData,
} from '../../types/i18n/i18nTypes';

// 오류 메시지 상수 (사용자 친화적인 메시지로 개선)
export const I18N_ERROR_MESSAGES = {
  // 필수 언어 관련 오류
  REQUIRED_KOREAN: '한국어 번역은 필수입니다',
  REQUIRED_NON_ENGLISH: '영어가 아닌 언어 중 최소 1개는 필수입니다 (한국어, 일본어, 중국어)',
  REQUIRED_FIELD: (field: string, lang: string) => {
    const fieldNames: Record<string, string> = {
      title: '제목',
      synopsis: '시놉시스',
      seo_title: 'SEO 제목',
      seo_synopsis: 'SEO 시놉시스',
      seo_keyword: 'SEO 키워드',
    };
    const langNames: Record<string, string> = {
      ko: '한국어',
      en: '영어',
      ja: '일본어',
      zh: '중국어',
    };
    return `${fieldNames[field] || field} 필드의 ${langNames[lang] || lang} 번역이 필수입니다`;
  },

  // 길이 제한 관련 오류
  FIELD_TOO_LONG: (field: string, lang: string, maxLength: number, currentLength: number) => {
    const fieldNames: Record<string, string> = {
      title: '제목',
      synopsis: '시놉시스',
      seo_title: 'SEO 제목',
      seo_synopsis: 'SEO 시놉시스',
      seo_keyword: 'SEO 키워드',
    };
    const langNames: Record<string, string> = {
      ko: '한국어',
      en: '영어',
      ja: '일본어',
      zh: '중국어',
    };
    return `${fieldNames[field] || field} 필드의 ${
      langNames[lang] || lang
    } 번역이 너무 깁니다 (현재: ${currentLength}자, 최대: ${maxLength}자)`;
  },

  // 형식 검증 관련 오류
  INVALID_FORMAT: (field: string, lang: string, reason: string) => {
    const fieldNames: Record<string, string> = {
      title: '제목',
      synopsis: '시놉시스',
      seo_title: 'SEO 제목',
      seo_synopsis: 'SEO 시놉시스',
      seo_keyword: 'SEO 키워드',
    };
    const langNames: Record<string, string> = {
      ko: '한국어',
      en: '영어',
      ja: '일본어',
      zh: '중국어',
    };
    return `${fieldNames[field] || field} 필드의 ${langNames[lang] || lang} 번역 형식이 올바르지 않습니다: ${reason}`;
  },

  // 언어 관련 오류
  INVALID_LANGUAGE: (lang: string) => `지원되지 않는 언어 코드입니다: ${lang}`,

  // 시스템 오류
  SYNC_ERROR: '레거시 필드와 i18n 필드 동기화에 실패했습니다',
  VALIDATION_ERROR: '다국어 데이터 검증에 실패했습니다',
  PRIORITY_MAPPING: '언어 우선순위에 따른 title 필드 매핑에 실패했습니다',
  EMPTY_CONTENT: '모든 언어의 콘텐츠가 비어있습니다',
  PRIORITY_MAPPING_FAILED: '언어 우선순위 매핑 처리 중 오류가 발생했습니다',
  API_VALIDATION_FAILED: 'API 검증에 실패했습니다',
  INCONSISTENT_MAPPING: '언어 우선순위와 실제 매핑 결과가 일치하지 않습니다',
} as const;

// 경고 메시지 상수
export const I18N_WARNING_MESSAGES = {
  MISSING_TRANSLATION: (field: string, lang: string) =>
    `${field} 필드의 ${lang} 번역이 누락되었습니다`,
  FALLBACK_USED: (field: string, lang: string) => `${field} 필드에서 ${lang} 폴백이 사용됩니다`,
  INCONSISTENT_LENGTH: (field: string) => `${field} 필드의 언어별 번역 길이가 크게 다릅니다`,
} as const;

// 필드별 최대 길이 설정 (i18nConfig에서 가져오도록 개선) - 사용하지 않음
// const getFieldMaxLength = (fieldName: I18nFieldName): number => {
//   // eslint-disable-next-line @typescript-eslint/no-require-imports
//   const { getFieldConfig } = require('../../../config/i18nConfig');
//   return getFieldConfig(fieldName.replace('i18n_', '')).maxLength;
// };

// 기본 필드별 최대 길이 설정 (폴백용)
const FIELD_MAX_LENGTHS: Record<I18nFieldName, number> = {
  title: 255,
  synopsis: 2000,
  seo_title: 255,
  seo_synopsis: 500,
  seo_keyword: 255,
  i18n_title: 255,
  i18n_synopsis: 2000,
  i18n_seo_title: 255,
  i18n_seo_synopsis: 500,
  i18n_seo_keyword: 255,
  name: 255,
  i18n_name: 255,
};

export class I18nValidation {
  /**
   * i18n 데이터 전체 검증
   * 요구사항 3.1: 영어가 아닌 언어(ko, zh_hans, zh_hant) 중 최소 1개는 필수로 검증
   */
  static validateI18nData(i18nData: ContentI18nData): I18nValidationResult {
    const errors: I18nValidationError[] = [];
    const warnings: I18nValidationWarning[] = [];

    // 전체 데이터 검증 (비영어 언어 필수 검증)
    const globalErrors = this.validateGlobalRules(i18nData);
    errors.push(...globalErrors);

    // 각 필드별 검증 (길이 제한 등)
    Object.entries(i18nData).forEach(([fieldName, fieldData]) => {
      const fieldErrors = this.validateFieldBasic(fieldName as I18nFieldName, fieldData);
      const fieldWarnings = this.validateFieldWarnings(fieldName as I18nFieldName, fieldData);

      errors.push(...fieldErrors);
      warnings.push(...fieldWarnings);
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * 필드별 기본 검증 (길이 제한, 형식 검증 등)
   */
  private static validateFieldBasic(
    fieldName: I18nFieldName,
    fieldData: I18nFieldData
  ): I18nValidationError[] {
    const errors: I18nValidationError[] = [];
    const enabledLanguages = getEnabledLanguages();

    enabledLanguages.forEach(language => {
      const value = fieldData[language];
      if (value) {
        // 길이 검증
        const maxLength = this.getFieldMaxLength(fieldName);
        if (value.length > maxLength) {
          errors.push({
            field: fieldName,
            language,
            message: I18N_ERROR_MESSAGES.FIELD_TOO_LONG(
              fieldName,
              language,
              maxLength,
              value.length
            ),
            code: 'FIELD_TOO_LONG',
          });
        }

        // 형식 검증
        const formatErrors = this.validateFieldFormat(fieldName, language, value);
        errors.push(...formatErrors);
      }
    });

    return errors;
  }

  /**
   * 필드별 최대 길이 가져오기
   */
  private static getFieldMaxLength(fieldName: I18nFieldName): number {
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const { getFieldConfig } = require('../../../config/i18nConfig');
      const cleanFieldName = fieldName.replace('i18n_', '');
      return getFieldConfig(cleanFieldName).maxLength;
    } catch {
      return FIELD_MAX_LENGTHS[fieldName] || 255;
    }
  }

  /**
   * 필드 형식 검증
   */
  private static validateFieldFormat(
    fieldName: I18nFieldName,
    language: LanguageCode,
    value: string
  ): I18nValidationError[] {
    const errors: I18nValidationError[] = [];

    // 빈 문자열이나 공백만 있는 경우
    if (!value.trim()) {
      return errors; // 빈 값은 다른 검증에서 처리
    }

    // HTML 태그 검증 (title, seo_title에서는 HTML 태그 금지)
    if (
      (fieldName === 'title' ||
        fieldName === 'seo_title' ||
        fieldName === 'i18n_title' ||
        fieldName === 'i18n_seo_title') &&
      /<[^>]*>/g.test(value)
    ) {
      errors.push({
        field: fieldName,
        language,
        message: I18N_ERROR_MESSAGES.INVALID_FORMAT(
          fieldName,
          language,
          'HTML 태그는 사용할 수 없습니다'
        ),
        code: 'INVALID_HTML_TAGS',
      });
    }

    // 특수 문자 검증 (SEO 키워드에서는 특정 특수문자만 허용)
    if (
      (fieldName === 'seo_keyword' || fieldName === 'i18n_seo_keyword') &&
      /[<>{}[\]\\|`~!@#$%^&*()+=?]/g.test(value)
    ) {
      errors.push({
        field: fieldName,
        language,
        message: I18N_ERROR_MESSAGES.INVALID_FORMAT(
          fieldName,
          language,
          '허용되지 않는 특수문자가 포함되어 있습니다'
        ),
        code: 'INVALID_SPECIAL_CHARS',
      });
    }

    // 연속된 공백 검증
    if (/\s{3,}/g.test(value)) {
      errors.push({
        field: fieldName,
        language,
        message: I18N_ERROR_MESSAGES.INVALID_FORMAT(
          fieldName,
          language,
          '연속된 공백은 2개까지만 허용됩니다'
        ),
        code: 'EXCESSIVE_WHITESPACE',
      });
    }

    // 줄바꿈 검증 (title, seo_title에서는 줄바꿈 금지)
    if (
      (fieldName === 'title' ||
        fieldName === 'seo_title' ||
        fieldName === 'i18n_title' ||
        fieldName === 'i18n_seo_title') &&
      /[\r\n]/g.test(value)
    ) {
      errors.push({
        field: fieldName,
        language,
        message: I18N_ERROR_MESSAGES.INVALID_FORMAT(
          fieldName,
          language,
          '줄바꿈은 사용할 수 없습니다'
        ),
        code: 'INVALID_LINE_BREAKS',
      });
    }

    return errors;
  }

  /**
   * 개별 언어 필수 검증 (특정 언어가 필수인 경우)
   * 요구사항 5.5: 필수 언어(기본값: ko)에 대한 누락된 번역을 감지하고 오류 메시지 제공
   */
  static validateRequiredLanguages(i18nData: ContentI18nData): I18nValidationError[] {
    const errors: I18nValidationError[] = [];
    const enabledLanguages = getEnabledLanguages();
    const requiredLanguages = getRequiredLanguages();

    // 각 필드별로 필수 언어 검증
    Object.entries(i18nData).forEach(([fieldName, fieldData]) => {
      requiredLanguages.forEach(language => {
        if (enabledLanguages.includes(language)) {
          const value = fieldData[language];

          // 필수 언어가 비어있거나 공백만 있는 경우
          if (!value || !value.trim()) {
            errors.push({
              field: fieldName as I18nFieldName,
              language,
              message: I18N_ERROR_MESSAGES.REQUIRED_FIELD(fieldName, language),
              code: 'REQUIRED_FIELD',
            });
          }
        }
      });
    });

    return errors;
  }

  /**
   * 한국어 필수 검증 (강화된 버전)
   * 요구사항 5.5: 한국어는 기본 필수 언어로 특별히 검증
   */
  static validateKoreanRequirement(i18nData: ContentI18nData): I18nValidationError[] {
    const errors: I18nValidationError[] = [];

    // title 필드는 항상 한국어 필수
    if (!i18nData.title?.ko?.trim()) {
      errors.push({
        field: 'title',
        language: 'ko',
        message: I18N_ERROR_MESSAGES.REQUIRED_KOREAN,
        code: 'REQUIRED_KOREAN',
      });
    }

    // synopsis가 다른 언어로 작성된 경우 한국어도 필수
    const hasSynopsisInOtherLanguages = Object.entries(i18nData.synopsis || {}).some(
      ([lang, value]) => lang !== 'ko' && value?.trim()
    );

    if (hasSynopsisInOtherLanguages && !i18nData.synopsis?.ko?.trim()) {
      errors.push({
        field: 'synopsis',
        language: 'ko',
        message: I18N_ERROR_MESSAGES.REQUIRED_KOREAN,
        code: 'REQUIRED_KOREAN',
      });
    }

    // SEO 필드들도 다른 언어가 있으면 한국어 필수
    (['seo_title', 'seo_synopsis', 'seo_keyword'] as const).forEach(seoField => {
      const seoData = i18nData[seoField];
      if (seoData) {
        const hasOtherLanguages = Object.entries(seoData).some(
          ([lang, value]) => lang !== 'ko' && value?.trim()
        );

        if (hasOtherLanguages && !seoData.ko?.trim()) {
          errors.push({
            field: seoField,
            language: 'ko',
            message: I18N_ERROR_MESSAGES.REQUIRED_KOREAN,
            code: 'REQUIRED_KOREAN',
          });
        }
      }
    });

    return errors;
  }

  /**
   * 필드별 경고 검증
   */
  private static validateFieldWarnings(
    fieldName: I18nFieldName,
    fieldData: I18nFieldData
  ): I18nValidationWarning[] {
    const warnings: I18nValidationWarning[] = [];
    const enabledLanguages = getEnabledLanguages();

    // 누락된 번역 경고
    enabledLanguages.forEach(language => {
      if (!fieldData[language]?.trim()) {
        warnings.push({
          field: fieldName,
          language,
          message: I18N_WARNING_MESSAGES.MISSING_TRANSLATION(fieldName, language),
          code: 'MISSING_TRANSLATION',
        });
      }
    });

    // 번역 길이 일관성 경고
    const lengths = enabledLanguages
      .map(lang => fieldData[lang]?.length || 0)
      .filter(length => length > 0);

    if (lengths.length > 1) {
      const maxLength = Math.max(...lengths);
      const minLength = Math.min(...lengths);

      if (maxLength > minLength * 2) {
        // 2배 이상 차이나면 경고
        warnings.push({
          field: fieldName,
          language: 'ko', // 기본 언어로 설정
          message: I18N_WARNING_MESSAGES.INCONSISTENT_LENGTH(fieldName),
          code: 'INCONSISTENT_LENGTH',
        });
      }
    }

    return warnings;
  }

  /**
   * 전역 규칙 검증
   * 요구사항 3.1: 영어가 아닌 언어 중 최소 1개는 필수
   */
  private static validateGlobalRules(i18nData: ContentI18nData): I18nValidationError[] {
    const errors: I18nValidationError[] = [];

    // 비영어 언어 필수 검증 - 각 필드별로 세밀하게 검증
    const titleHasNonEnglish = NON_ENGLISH_LANGUAGES.some(language =>
      i18nData.title?.[language]?.trim()
    );
    const synopsisHasNonEnglish = NON_ENGLISH_LANGUAGES.some(language =>
      i18nData.synopsis?.[language]?.trim()
    );

    // title 필드에 비영어 언어가 없으면 오류
    if (!titleHasNonEnglish) {
      errors.push({
        field: 'title',
        language: 'ko', // 기본 언어로 설정
        message: I18N_ERROR_MESSAGES.REQUIRED_NON_ENGLISH,
        code: 'REQUIRED_NON_ENGLISH',
      });
    }

    // synopsis 필드에 비영어 언어가 없으면 오류 (synopsis가 있는 경우에만)
    const hasSynopsisContent = Object.values(i18nData.synopsis || {}).some(value => value?.trim());
    if (hasSynopsisContent && !synopsisHasNonEnglish) {
      errors.push({
        field: 'synopsis',
        language: 'ko', // 기본 언어로 설정
        message: I18N_ERROR_MESSAGES.REQUIRED_NON_ENGLISH,
        code: 'REQUIRED_NON_ENGLISH',
      });
    }

    // 전체 콘텐츠 비어있음 검증
    const hasAnyContent = Object.values(i18nData).some(fieldData =>
      Object.values(fieldData || {}).some(value => typeof value === 'string' && value.trim())
    );

    if (!hasAnyContent) {
      errors.push({
        field: 'title',
        language: 'ko', // 기본 언어로 설정
        message: I18N_ERROR_MESSAGES.EMPTY_CONTENT,
        code: 'EMPTY_CONTENT',
      });
    }

    return errors;
  }

  /**
   * 특정 필드의 특정 언어 검증 (강화된 버전)
   */
  static validateFieldValue(
    fieldName: I18nFieldName,
    language: LanguageCode,
    value: string
  ): I18nValidationError[] {
    const errors: I18nValidationError[] = [];
    const requiredLanguages = getRequiredLanguages();

    // 필수 필드 검증
    if (requiredLanguages.includes(language) && !value?.trim()) {
      errors.push({
        field: fieldName,
        language,
        message: I18N_ERROR_MESSAGES.REQUIRED_FIELD(fieldName, language),
        code: 'REQUIRED_FIELD',
      });
    }

    // 한국어 특별 검증
    if (language === 'ko' && !value?.trim()) {
      // title은 항상 한국어 필수
      if (fieldName === 'title' || fieldName === 'i18n_title') {
        errors.push({
          field: fieldName,
          language,
          message: I18N_ERROR_MESSAGES.REQUIRED_KOREAN,
          code: 'REQUIRED_KOREAN',
        });
      }
    }

    // 값이 있는 경우에만 추가 검증 수행
    if (value) {
      // 길이 검증
      const maxLength = this.getFieldMaxLength(fieldName);
      if (value.length > maxLength) {
        errors.push({
          field: fieldName,
          language,
          message: I18N_ERROR_MESSAGES.FIELD_TOO_LONG(fieldName, language, maxLength, value.length),
          code: 'FIELD_TOO_LONG',
        });
      }

      // 형식 검증
      const formatErrors = this.validateFieldFormat(fieldName, language, value);
      errors.push(...formatErrors);
    }

    return errors;
  }

  /**
   * 언어 코드 유효성 검증
   */
  static validateLanguageCode(languageCode: string): boolean {
    const enabledLanguages = getEnabledLanguages();
    return enabledLanguages.includes(languageCode as LanguageCode);
  }

  /**
   * 언어 우선순위 일관성 검증
   * 요구사항 3.2, 3.5: 우선순위에 따른 매핑이 올바른지 검증
   */
  static validateLanguagePriorityConsistency(
    i18nData: I18nFieldData,
    expectedTitle: string,
    expectedTitleEn?: string
  ): I18nValidationError[] {
    const errors: I18nValidationError[] = [];

    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const { I18nService } = require('./i18nService');
      const mapping = I18nService.mapTitleByPriority(i18nData);

      // 예상 title과 실제 매핑 결과 비교
      if (mapping.title !== expectedTitle) {
        errors.push({
          field: 'title',
          language: mapping.sourceLanguage,
          message: `언어 우선순위 매핑 불일치: 예상="${expectedTitle}", 실제="${mapping.title}"`,
          code: 'INCONSISTENT_MAPPING',
        });
      }

      // 예상 title_en과 실제 매핑 결과 비교
      if (expectedTitleEn !== undefined && mapping.title_en !== expectedTitleEn) {
        errors.push({
          field: 'title',
          language: 'en',
          message: `영어 title 매핑 불일치: 예상="${expectedTitleEn}", 실제="${mapping.title_en}"`,
          code: 'INCONSISTENT_MAPPING',
        });
      }
    } catch {
      errors.push({
        field: 'title',
        language: 'ko', // 기본 언어로 설정
        message: I18N_ERROR_MESSAGES.PRIORITY_MAPPING,
        code: 'PRIORITY_MAPPING_ERROR',
      });
    }

    return errors;
  }

  /**
   * 비영어 언어 필수 검증 (강화된 버전)
   * 요구사항 3.1: 영어가 아닌 언어 중 최소 1개는 필수 (필드별 세밀한 검증)
   */
  static validateNonEnglishRequirement(
    i18nData: ContentI18nData,
    strictMode: boolean = false
  ): I18nValidationError[] {
    const errors: I18nValidationError[] = [];

    // title 필드 검증 (항상 필수)
    const titleHasNonEnglish = NON_ENGLISH_LANGUAGES.some(language =>
      i18nData.title?.[language]?.trim()
    );

    if (!titleHasNonEnglish) {
      errors.push({
        field: 'title',
        language: 'ko', // 기본 언어로 설정
        message: 'title 필드에 ' + I18N_ERROR_MESSAGES.REQUIRED_NON_ENGLISH,
        code: 'REQUIRED_NON_ENGLISH',
      });
    }

    // synopsis 필드 검증 (strictMode이거나 내용이 있는 경우)
    const hasSynopsisContent = Object.values(i18nData.synopsis || {}).some(value => value?.trim());
    const synopsisHasNonEnglish = NON_ENGLISH_LANGUAGES.some(language =>
      i18nData.synopsis?.[language]?.trim()
    );

    if ((strictMode || hasSynopsisContent) && !synopsisHasNonEnglish && hasSynopsisContent) {
      errors.push({
        field: 'synopsis',
        language: 'ko', // 기본 언어로 설정
        message: 'synopsis 필드에 ' + I18N_ERROR_MESSAGES.REQUIRED_NON_ENGLISH,
        code: 'REQUIRED_NON_ENGLISH',
      });
    }

    return errors;
  }

  /**
   * 실시간 검증 (입력 중)
   */
  static validateRealtime(
    fieldName: I18nFieldName,
    language: LanguageCode,
    value: string
  ): { isValid: boolean; message?: string } {
    const errors = this.validateFieldValue(fieldName, language, value);

    if (errors.length > 0) {
      return {
        isValid: false,
        message: errors[0].message,
      };
    }

    return { isValid: true };
  }

  /**
   * title 필드 매핑 검증
   * 요구사항 3.5: 언어 우선순위에 따른 title 필드 매핑 검증
   */
  static validateTitleMapping(i18nData: ContentI18nData): I18nValidationResult {
    const errors: I18nValidationError[] = [];
    const warnings: I18nValidationWarning[] = [];

    try {
      // I18nService를 동적으로 import하여 순환 참조 방지
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const { I18nService } = require('./i18nService');

      // title 필드 매핑 검증
      const titleMapping = I18nService.mapTitleByPriority(i18nData.title);

      // 매핑 결과가 비어있으면 오류
      if (!titleMapping.title) {
        errors.push({
          field: 'title',
          language: 'ko', // 기본 언어로 설정
          message: I18N_ERROR_MESSAGES.PRIORITY_MAPPING,
          code: 'PRIORITY_MAPPING_FAILED',
        });
      }

      // 폴백이 사용된 경우 경고
      if (titleMapping.fallbackUsed && titleMapping.sourceLanguage === 'en') {
        warnings.push({
          field: 'title',
          language: 'en',
          message: I18N_WARNING_MESSAGES.FALLBACK_USED('title', 'English'),
          code: 'FALLBACK_USED',
        });
      }

      // synopsis 필드도 동일하게 검증 (내용이 있는 경우)
      const hasSynopsisContent = Object.values(i18nData.synopsis || {}).some(
        value => typeof value === 'string' && value?.trim()
      );
      if (hasSynopsisContent) {
        const synopsisMapping = I18nService.mapTitleByPriority(i18nData.synopsis);

        if (!synopsisMapping.title) {
          errors.push({
            field: 'synopsis',
            language: 'ko', // 기본 언어로 설정
            message: I18N_ERROR_MESSAGES.PRIORITY_MAPPING,
            code: 'PRIORITY_MAPPING_FAILED',
          });
        }

        if (synopsisMapping.fallbackUsed && synopsisMapping.sourceLanguage === 'en') {
          warnings.push({
            field: 'synopsis',
            language: 'en',
            message: I18N_WARNING_MESSAGES.FALLBACK_USED('synopsis', 'English'),
            code: 'FALLBACK_USED',
          });
        }
      }
    } catch {
      errors.push({
        field: 'title',
        language: 'ko', // 기본 언어로 설정
        message: I18N_ERROR_MESSAGES.PRIORITY_MAPPING,
        code: 'PRIORITY_MAPPING_ERROR',
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * 포괄적 i18n 검증 (데이터 검증 + 매핑 검증)
   * 요구사항 3.1, 3.5: 전체 검증 프로세스
   */
  static validateComprehensive(i18nData: ContentI18nData): I18nValidationResult {
    // 기본 데이터 검증
    const dataValidation = this.validateI18nData(i18nData);

    // title 매핑 검증
    const mappingValidation = this.validateTitleMapping(i18nData);

    // 결과 병합
    return {
      isValid: dataValidation.isValid && mappingValidation.isValid,
      errors: [...dataValidation.errors, ...mappingValidation.errors],
      warnings: [...dataValidation.warnings, ...mappingValidation.warnings],
    };
  }

  /**
   * API 오류 응답 처리
   * 요구사항 6.3: API 오류가 발생할 때 i18n 관련 검증 실패에 대한 명확한 오류 메시지 제공
   */
  static parseApiValidationError(apiError: any): I18nValidationResult {
    const errors: I18nValidationError[] = [];
    const warnings: I18nValidationWarning[] = [];

    try {
      // API 오류 응답 구조 분석
      const errorData = apiError.response?.data || apiError.data || apiError;

      if (errorData.errors) {
        // 구조화된 오류 응답 처리
        errorData.errors.forEach((error: any) => {
          if (error.field && error.code) {
            errors.push({
              field: error.field as I18nFieldName,
              language: error.language as LanguageCode,
              message: error.message || I18N_ERROR_MESSAGES.VALIDATION_ERROR,
              code: error.code,
            });
          }
        });
      } else if (errorData.message) {
        // 단순 메시지 오류 처리
        const message = errorData.message;

        // i18n 관련 오류 패턴 매칭
        if (
          message.includes('non-English') ||
          message.includes('비영어') ||
          message.includes('영어가 아닌')
        ) {
          errors.push({
            field: 'title',
            language: 'ko', // 기본 언어로 설정
            message: I18N_ERROR_MESSAGES.REQUIRED_NON_ENGLISH,
            code: 'REQUIRED_NON_ENGLISH',
          });
        } else if (message.includes('priority') || message.includes('우선순위')) {
          errors.push({
            field: 'title',
            language: 'ko', // 기본 언어로 설정
            message: I18N_ERROR_MESSAGES.PRIORITY_MAPPING,
            code: 'PRIORITY_MAPPING_FAILED',
          });
        } else if (message.includes('sync') || message.includes('동기화')) {
          errors.push({
            field: 'title',
            language: 'ko', // 기본 언어로 설정
            message: I18N_ERROR_MESSAGES.SYNC_ERROR,
            code: 'SYNC_ERROR',
          });
        } else {
          errors.push({
            field: 'title',
            language: 'ko', // 기본 언어로 설정
            message: message,
            code: 'API_VALIDATION_ERROR',
          });
        }
      } else {
        // 알 수 없는 오류 형식
        errors.push({
          field: 'title',
          language: 'ko', // 기본 언어로 설정
          message: I18N_ERROR_MESSAGES.VALIDATION_ERROR,
          code: 'UNKNOWN_API_ERROR',
        });
      }
    } catch {
      errors.push({
        field: 'title',
        language: 'ko', // 기본 언어로 설정
        message: I18N_ERROR_MESSAGES.VALIDATION_ERROR,
        code: 'ERROR_PARSE_FAILED',
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * 완전한 i18n 검증 (모든 검증 규칙 적용)
   * 요구사항 5.5, 7.3: 필수 언어 검증 및 사용자 친화적 오류 메시지
   */
  static validateComplete(
    i18nData: ContentI18nData,
    options: {
      strictMode?: boolean;
      validateMapping?: boolean;
      validateRequiredLanguages?: boolean;
      validateKorean?: boolean;
    } = {}
  ): I18nValidationResult {
    const {
      strictMode = false,
      validateMapping = true,
      validateRequiredLanguages = true, // 기본값을 true로 변경
      validateKorean = true, // 한국어 검증 추가
    } = options;

    const allErrors: I18nValidationError[] = [];
    const allWarnings: I18nValidationWarning[] = [];

    // 1. 기본 i18n 데이터 검증 (비영어 언어 필수, 길이 제한, 형식 검증 등)
    const dataValidation = this.validateI18nData(i18nData);
    allErrors.push(...dataValidation.errors);
    allWarnings.push(...dataValidation.warnings);

    // 2. 한국어 필수 검증 (요구사항 5.5)
    if (validateKorean) {
      const koreanErrors = this.validateKoreanRequirement(i18nData);
      allErrors.push(...koreanErrors);
    }

    // 3. 개별 언어 필수 검증
    if (validateRequiredLanguages) {
      const requiredLanguageErrors = this.validateRequiredLanguages(i18nData);
      allErrors.push(...requiredLanguageErrors);
    }

    // 4. 비영어 언어 필수 검증 (강화된 버전)
    const nonEnglishErrors = this.validateNonEnglishRequirement(i18nData, strictMode);
    allErrors.push(...nonEnglishErrors);

    // 5. title 필드 매핑 검증 (옵션)
    if (validateMapping) {
      const mappingValidation = this.validateTitleMapping(i18nData);
      allErrors.push(...mappingValidation.errors);
      allWarnings.push(...mappingValidation.warnings);
    }

    // 중복 제거 (더 정확한 중복 제거 로직)
    const uniqueErrors = this.removeDuplicateErrors(allErrors);
    const uniqueWarnings = this.removeDuplicateWarnings(allWarnings);

    return {
      isValid: uniqueErrors.length === 0,
      errors: uniqueErrors,
      warnings: uniqueWarnings,
    };
  }

  /**
   * 중복 오류 제거
   */
  private static removeDuplicateErrors(errors: I18nValidationError[]): I18nValidationError[] {
    const seen = new Set<string>();
    return errors.filter(error => {
      const key = `${error.field}-${error.code}-${error.language || 'none'}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  /**
   * 중복 경고 제거
   */
  private static removeDuplicateWarnings(
    warnings: I18nValidationWarning[]
  ): I18nValidationWarning[] {
    const seen = new Set<string>();
    return warnings.filter(warning => {
      const key = `${warning.field}-${warning.code}-${warning.language || 'none'}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  /**
   * 검증 결과 요약
   */
  static summarizeValidation(result: I18nValidationResult): string {
    if (result.isValid) {
      return '모든 검증을 통과했습니다.';
    }

    const errorCount = result.errors.length;
    const warningCount = result.warnings.length;

    return `검증 실패: 오류 ${errorCount}개, 경고 ${warningCount}개`;
  }

  /**
   * 검증 결과 상세 요약
   */
  static summarizeValidationDetailed(result: I18nValidationResult): {
    summary: string;
    errorsByField: Record<string, I18nValidationError[]>;
    warningsByField: Record<string, I18nValidationWarning[]>;
    errorCounts: Record<string, number>;
  } {
    const errorsByField: Record<string, I18nValidationError[]> = {};
    const warningsByField: Record<string, I18nValidationWarning[]> = {};
    const errorCounts: Record<string, number> = {};

    // 필드별 오류 분류
    result.errors.forEach(error => {
      if (!errorsByField[error.field]) {
        errorsByField[error.field] = [];
      }
      errorsByField[error.field].push(error);

      // 오류 코드별 카운트
      errorCounts[error.code || 'UNKNOWN'] = (errorCounts[error.code || 'UNKNOWN'] || 0) + 1;
    });

    // 필드별 경고 분류
    result.warnings.forEach(warning => {
      if (!warningsByField[warning.field]) {
        warningsByField[warning.field] = [];
      }
      warningsByField[warning.field].push(warning);
    });

    return {
      summary: this.summarizeValidation(result),
      errorsByField,
      warningsByField,
      errorCounts,
    };
  }

  /**
   * 사용자 친화적인 검증 결과 메시지 생성
   * 요구사항 5.5, 7.3: 사용자가 이해하기 쉬운 오류 메시지 제공
   */
  static generateUserFriendlyMessage(result: I18nValidationResult): string {
    if (result.isValid) {
      return '✅ 모든 검증을 통과했습니다!';
    }

    const messages: string[] = [];

    // 필수 언어 오류 우선 처리
    const koreanErrors = result.errors.filter(e => e.code === 'REQUIRED_KOREAN');
    if (koreanErrors.length > 0) {
      messages.push('❌ 한국어 번역이 필요합니다:');
      koreanErrors.forEach(error => {
        const fieldName = this.getFieldDisplayName(error.field);
        messages.push(`   • ${fieldName}`);
      });
    }

    // 필수 필드 오류
    const requiredErrors = result.errors.filter(e => e.code === 'REQUIRED_FIELD');
    if (requiredErrors.length > 0) {
      messages.push('❌ 필수 필드가 누락되었습니다:');
      requiredErrors.forEach(error => {
        const fieldName = this.getFieldDisplayName(error.field);
        const langName = this.getLanguageDisplayName(error.language);
        messages.push(`   • ${fieldName} (${langName})`);
      });
    }

    // 길이 초과 오류
    const lengthErrors = result.errors.filter(e => e.code === 'FIELD_TOO_LONG');
    if (lengthErrors.length > 0) {
      messages.push('❌ 입력 길이가 초과되었습니다:');
      lengthErrors.forEach(error => {
        const fieldName = this.getFieldDisplayName(error.field);
        const langName = this.getLanguageDisplayName(error.language);
        messages.push(`   • ${fieldName} (${langName})`);
      });
    }

    // 형식 오류
    const formatErrors = result.errors.filter(e =>
      [
        'INVALID_HTML_TAGS',
        'INVALID_SPECIAL_CHARS',
        'EXCESSIVE_WHITESPACE',
        'INVALID_LINE_BREAKS',
      ].includes(e.code || '')
    );
    if (formatErrors.length > 0) {
      messages.push('❌ 입력 형식이 올바르지 않습니다:');
      formatErrors.forEach(error => {
        const fieldName = this.getFieldDisplayName(error.field);
        const langName = this.getLanguageDisplayName(error.language);
        messages.push(
          `   • ${fieldName} (${langName}): ${this.getFormatErrorDescription(error.code || '')}`
        );
      });
    }

    // 경고 메시지
    if (result.warnings.length > 0) {
      messages.push('⚠️ 주의사항:');
      result.warnings.forEach(warning => {
        const fieldName = this.getFieldDisplayName(warning.field);
        const langName = this.getLanguageDisplayName(warning.language);
        messages.push(`   • ${fieldName} (${langName}): ${warning.message}`);
      });
    }

    return messages.join('\n');
  }

  /**
   * 필드명을 사용자 친화적인 이름으로 변환
   */
  private static getFieldDisplayName(field: string): string {
    const fieldNames: Record<string, string> = {
      title: '제목',
      synopsis: '시놉시스',
      seo_title: 'SEO 제목',
      seo_synopsis: 'SEO 시놉시스',
      seo_keyword: 'SEO 키워드',
      i18n_title: '제목',
      i18n_synopsis: '시놉시스',
      i18n_seo_title: 'SEO 제목',
      i18n_seo_synopsis: 'SEO 시놉시스',
      i18n_seo_keyword: 'SEO 키워드',
    };
    return fieldNames[field] || field;
  }

  /**
   * 언어 코드를 사용자 친화적인 이름으로 변환
   */
  private static getLanguageDisplayName(language?: LanguageCode): string {
    if (!language) return '';
    const langNames: Record<string, string> = {
      ko: '한국어',
      en: '영어',
      ja: '일본어',
      zh: '중국어',
    };
    return langNames[language] || language;
  }

  /**
   * 형식 오류 코드를 설명으로 변환
   */
  private static getFormatErrorDescription(code: string): string {
    const descriptions: Record<string, string> = {
      INVALID_HTML_TAGS: 'HTML 태그 사용 불가',
      INVALID_SPECIAL_CHARS: '허용되지 않는 특수문자',
      EXCESSIVE_WHITESPACE: '연속된 공백 초과',
      INVALID_LINE_BREAKS: '줄바꿈 사용 불가',
    };
    return descriptions[code] || '형식 오류';
  }

  /**
   * 빠른 검증 (필수 검증만)
   * 실시간 입력 시 사용
   */
  static validateQuick(i18nData: ContentI18nData): I18nValidationResult {
    const errors: I18nValidationError[] = [];
    const warnings: I18nValidationWarning[] = [];

    // 한국어 필수 검증만 수행
    const koreanErrors = this.validateKoreanRequirement(i18nData);
    errors.push(...koreanErrors);

    // 기본 길이 검증만 수행
    Object.entries(i18nData).forEach(([fieldName, fieldData]) => {
      if (fieldData) {
        Object.entries(fieldData).forEach(([language, value]) => {
          if (value && typeof value === 'string') {
            const maxLength = this.getFieldMaxLength(fieldName as I18nFieldName);
            if (value.length > maxLength) {
              errors.push({
                field: fieldName as I18nFieldName,
                language: language as LanguageCode,
                message: I18N_ERROR_MESSAGES.FIELD_TOO_LONG(
                  fieldName,
                  language,
                  maxLength,
                  value.length
                ),
                code: 'FIELD_TOO_LONG',
              });
            }
          }
        });
      }
    });

    return {
      isValid: errors.length === 0,
      errors: this.removeDuplicateErrors(errors),
      warnings,
    };
  }
}
